import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Truck, Award, TrendingUp } from "lucide-react";

const HomePage = () => {
  const [prices, setPrices] = useState({ gold: 0, silver: 0 });
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pricesRes, productsRes] = await Promise.all([
        axios.get(`${API}/prices/live`),
        axios.get(`${API}/products`)
      ]);
      setPrices({
        gold: pricesRes.data.gold?.rate_per_gram || 0,
        silver: pricesRes.data.silver?.rate_per_gram || 0
      });
      // Get 4 featured products (2 gold, 2 silver)
      const gold = productsRes.data.filter(p => p.metal_type === 'gold').slice(0, 2);
      const silver = productsRes.data.filter(p => p.metal_type === 'silver').slice(0, 2);
      setFeaturedProducts([...gold, ...silver]);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden" data-testid="hero-section">
        <div 
          className="hero-bg"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1767533427305-7a1f61c92fb3?w=1920)` }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4" data-testid="hero-overline">
              Premium Bullion Since 2020
            </p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-tight mb-6" data-testid="hero-title">
              Divine Purity.<br />
              <span className="text-[#D4AF37]">Real-Time Value.</span>
            </h1>
            <p className="text-lg md:text-xl text-[#A3A3A3] leading-relaxed mb-8 max-w-xl" data-testid="hero-description">
              Invest in BIS Hallmarked gold and silver coins with live market pricing. 
              Free insured shipping across India.
            </p>

            {/* Live Prices */}
            <div className="flex flex-wrap gap-6 mb-10" data-testid="hero-prices">
              <div className="glass px-6 py-4">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Gold Rate</p>
                <p className="text-3xl font-light tracking-tighter text-[#D4AF37]">
                  ₹{prices.gold.toLocaleString()}<span className="text-sm text-[#737373]">/g</span>
                </p>
              </div>
              <div className="glass px-6 py-4">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Silver Rate</p>
                <p className="text-3xl font-light tracking-tighter text-[#C0C0C0]">
                  ₹{prices.silver.toLocaleString()}<span className="text-sm text-[#737373]">/g</span>
                </p>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link to="/products/gold" className="btn-gold flex items-center gap-2" data-testid="cta-gold">
                Shop Gold Coins <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products/silver" className="btn-outline flex items-center gap-2" data-testid="cta-silver">
                Shop Silver Coins <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-[#0a0a0a] border-y border-[rgba(255,255,255,0.05)]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div {...fadeInUp} className="text-center">
              <Shield className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">BIS Hallmarked</h3>
              <p className="text-sm text-[#737373]">Certified purity guaranteed</p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ delay: 0.1 }} className="text-center">
              <Truck className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">Free Shipping</h3>
              <p className="text-sm text-[#737373]">Insured delivery pan-India</p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="text-center">
              <TrendingUp className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">Live Pricing</h3>
              <p className="text-sm text-[#737373]">Updated every 2 minutes</p>
            </motion.div>
            <motion.div {...fadeInUp} transition={{ delay: 0.3 }} className="text-center">
              <Award className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-1">2% Affiliate</h3>
              <p className="text-sm text-[#737373]">Earn on every referral</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Cards */}
      <section className="py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            className="text-4xl font-semibold tracking-tight text-white mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Shop by Metal
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Gold Category */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/products/gold" className="block group" data-testid="category-gold">
                <div className="relative overflow-hidden bg-[#121212] border border-[rgba(255,255,255,0.05)] hover:border-[rgba(212,175,55,0.3)] transition-all duration-300">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_metal-vault-store/artifacts/4uf722vx_gold.jpeg"
                      alt="Gold Coins"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-2">24K Pure (999.9)</p>
                    <h3 className="text-2xl font-semibold text-white mb-2">Gold Lakshmi Coins</h3>
                    <p className="text-[#A3A3A3] mb-4">100mg to 8g • BIS Hallmarked</p>
                    <span className="text-[#D4AF37] flex items-center gap-2 group-hover:gap-3 transition-all">
                      Explore Collection <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Silver Category */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/products/silver" className="block group" data-testid="category-silver">
                <div className="relative overflow-hidden bg-[#121212] border border-[rgba(255,255,255,0.05)] hover:border-[rgba(192,192,192,0.3)] transition-all duration-300">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_metal-vault-store/artifacts/di4r1git_silver.jpeg"
                      alt="Silver Coins"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C0C0C0] mb-2">999 Fine Silver</p>
                    <h3 className="text-2xl font-semibold text-white mb-2">Silver Lakshmi Coins</h3>
                    <p className="text-[#A3A3A3] mb-4">5g to 100g • Investment Grade</p>
                    <span className="text-[#C0C0C0] flex items-center gap-2 group-hover:gap-3 transition-all">
                      Explore Collection <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-24 bg-[#0a0a0a]" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Best Sellers</p>
                <h2 className="text-4xl font-semibold tracking-tight text-white">Featured Products</h2>
              </div>
              <Link to="/products" className="text-[#A3A3A3] hover:text-[#D4AF37] flex items-center gap-2 transition-colors">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/product/${product.id}`} data-testid={`featured-product-${index}`}>
                    <div className={`product-card ${product.metal_type}`}>
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="product-image"
                      />
                      <div className="p-4">
                        <span className={`badge-${product.metal_type} inline-block mb-2`}>
                          {product.purity} {product.metal_type}
                        </span>
                        <h3 className="text-white font-medium mb-2 line-clamp-1">{product.name}</h3>
                        <p className={`price-tag ${product.metal_type} text-2xl`}>
                          ₹{product.total_price?.toLocaleString()}
                        </p>
                        <p className="text-xs text-[#737373] mt-1">
                          Incl. Making Charges & GST
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Affiliate CTA */}
      <section className="py-24" data-testid="affiliate-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass p-12 md:p-16 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Partner Program</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Earn 2% Commission
            </h2>
            <p className="text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-8">
              Join our affiliate program and earn 2% on every successful referral. 
              Access marketing assets, track your earnings, and connect with other partners in our exclusive forum.
            </p>
            <Link to="/register" className="btn-gold inline-flex items-center gap-2" data-testid="affiliate-cta-btn">
              Become an Affiliate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
